import React, { useRef, useState } from "react";
import { Box, Button, HStack, Link, Text, VStack, useColorModeValue, useToast } from "@chakra-ui/react";
import { Controller } from "react-hook-form";
import { BsPaperclip, BsUpload } from "react-icons/bs";
import FieldFrame from "./FieldFrame";
import riskReviewService from "../../../services/riskReviewService";

const getAttachments = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const FileUploadField = ({ field, control, disabled, error, required, requestId }) => {
  const bg = useColorModeValue("gray.50", "whiteAlpha.50");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");
  const inputRef = useRef(null);
  const toast = useToast();
  const [isUploading, setIsUploading] = useState(false);

  return (
    <Controller
      control={control}
      name={field.code}
      render={({ field: input }) => {
        const attachments = getAttachments(input.value);
        const accept =
          field.inputType === "EXCEL_UPLOAD"
            ? ".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : undefined;

        const handleFilesSelected = async (event) => {
          const files = Array.from(event.target.files || []);
          if (!files.length) return;

          if (!requestId) {
            toast({
              title: "Save the request before uploading attachments.",
              status: "warning",
            });
            return;
          }

          try {
            setIsUploading(true);
            const uploaded = await riskReviewService.uploadAttachments({
              id: requestId,
              fieldCode: field.code,
              files,
            });
            input.onChange(uploaded);
            toast({ title: "Attachment uploaded", status: "success" });
          } catch (uploadError) {
            toast({
              title: uploadError?.response?.data?.message || uploadError?.message || "Failed to upload attachment.",
              status: "error",
            });
          } finally {
            setIsUploading(false);
            if (inputRef.current) inputRef.current.value = "";
          }
        };

        return (
          <FieldFrame field={field} error={error} isRequired={required}>
            <Box bg={bg} borderWidth="1px" borderColor={borderColor} borderStyle="dashed" borderRadius="md" p={4}>
              <input
                ref={inputRef}
                type="file"
                accept={accept}
                multiple
                hidden
                disabled={disabled || isUploading}
                onChange={handleFilesSelected}
              />
              <HStack justify="space-between" align="flex-start" spacing={3} wrap="wrap">
                <Box>
                  <Text fontWeight="medium">Attachments</Text>
                  <Text color="gray.500" fontSize="sm">
                    Upload files for this field.
                  </Text>
                </Box>
                <Button
                  size="sm"
                  leftIcon={<BsUpload />}
                  onClick={() => inputRef.current?.click()}
                  isDisabled={disabled || isUploading}
                  isLoading={isUploading}
                >
                  Upload
                </Button>
              </HStack>

              {attachments.length ? (
                <VStack align="stretch" spacing={2} mt={3}>
                  {attachments.map((attachment, index) => (
                    <HStack key={`${attachment.storageKey || attachment.url || attachment.fileName}-${index}`} spacing={2}>
                      <BsPaperclip color="var(--chakra-colors-gray-500)" />
                      {attachment.url ? (
                        <Link href={attachment.url} isExternal color="blue.600" fontSize="sm" fontWeight="medium">
                          {attachment.originalName || attachment.fileName || "Attachment"}
                        </Link>
                      ) : (
                        <Text color="gray.700" fontSize="sm" fontWeight="medium">
                          {attachment.originalName || attachment.fileName || "Attachment"}
                        </Text>
                      )}
                    </HStack>
                  ))}
                </VStack>
              ) : null}
            </Box>
          </FieldFrame>
        );
      }}
    />
  );
};

export default FileUploadField;
